import React, { useState, useRef, useEffect } from 'react';
import html2pdf from 'html2pdf.js';
import { DataManager } from '../../services/dataManager';

const EditableReportModal = ({
    client,
    meetings = [],
    works = [],
    counselor,
    supervisionKeywords = {},
    bioAnalysis = [],
    toolStats = { breathing: 0, cards: 0, frequencies: 0, dreams: 0, experiments: 0 },
    questionnaireAnswers = {},
    initialContractId = null,
    onClose
}) => {
    const reportRef = useRef(null);
    const [downloading, setDownloading] = useState(false);
    const [copied, setCopied] = useState(false);

    const clientContracts = DataManager.getClientContracts(client);
    const [selectedContractId, setSelectedContractId] = useState(initialContractId || (clientContracts.length > 0 ? clientContracts[0].id : 'all'));

    // Initial State derived from props and selected contract
    const getInitialReportData = (contractId = selectedContractId) => {
        const counselorName = counselor?.name || "Counselor";
        const qAnswers = questionnaireAnswers || {};
        const clientName = client?.name || "Cliente";
        
        let age = qAnswers.age || client?.age;
        if (!age && client?.birthDate) {
            const dob = new Date(client.birthDate);
            const diffMs = Date.now() - dob.getTime();
            const ageDt = new Date(diffMs); 
            const calcAge = Math.abs(ageDt.getUTCFullYear() - 1970);
            if (!isNaN(calcAge)) age = `${calcAge}`;
        }
        if (!age) age = 'N/D';

        const profession = qAnswers.profession || client?.profession || client?.workSituation || 'N/D';
        
        const activeContract = clientContracts.find(c => c.id === contractId);
        let contractText = 'Definito in sessione';
        let contractMeetings = meetings;

        if (activeContract) {
            contractText = `${activeContract.title}${activeContract.description ? ' — ' + activeContract.description : ''}${activeContract.startDate ? ' (Dal ' + new Date(activeContract.startDate).toLocaleDateString('it-IT') + ')' : ''}`;
            const filtered = meetings.filter(m => m.contractId === activeContract.id);
            if (filtered.length > 0) {
                contractMeetings = filtered;
            }
        } else if (client?.contract?.title || client?.contract?.description) {
            contractText = `${client.contract.title || ''}${client.contract.description ? ' — ' + client.contract.description : ''}`;
        }

        // Keywords list
        const kwList = Object.entries(supervisionKeywords)
            .sort((a, b) => b[1].count - a[1].count)
            .slice(0, 20)
            .map(([k, v]) => `${v.display || k} (${v.count})`)
            .join(', ');

        // Bio analysis initial text
        const bioText = bioAnalysis.slice(0, 3).map((item, idx) => 
            `${idx + 1}. ${item.name} (${item.probability}%)\nFerita: ${item.wound}\n${item.description}`
        ).join('\n\n');

        // Client app usage
        const clientWorks = works ? works.filter(w => ['diary', 'journal', 'dream', 'at_experiment', 'somatic_experiment'].includes(w.type)) : [];
        const breathingWorks = works ? works.filter(w => w.type === 'breathing_session') : [];
        const appUsage = clientWorks.length + breathingWorks.length > 0
            ? `Il cliente ha utilizzato l'app in autonomia registrando ${clientWorks.length} note/diari e ${breathingWorks.length} sessioni di respiro.`
            : `Il cliente non ha utilizzato l'app in autonomia. Tutti i dati sono stati registrati in seduta.`;

        const meetingInfo = activeContract && contractMeetings.length !== meetings.length
            ? `Incontri Contratto: ${contractMeetings.length} (Totali: ${meetings.length})`
            : `Incontri: ${meetings.length}`;

        return {
            title: 'AwarenessTool',
            subtitle: 'SUPERVISIONE PROFESSIONALE BIOGESTALT',
            date: new Date().toLocaleDateString('it-IT'),
            clientName: clientName,
            counselorName: counselorName,
            anagrafica: `Età: ${age} anni • Professione: ${profession} • ${meetingInfo}`,
            contract: `Obiettivo di Percorso / Contratto: ${contractText}`,
            bioenergetics: bioText || 'Nessuna evidenza bioenergetica specifica.',
            semanticMapText: kwList || 'Nessun tag registrato.',
            toolsSummary: `Respirazione: ${toolStats.breathing} • Carte: ${toolStats.cards} • Frequenze: ${toolStats.frequencies} • Lavoro Onirico: ${toolStats.dreams} • Esperimenti/Lab: ${toolStats.experiments}`,
            appUsage: appUsage,
            clinicalSynthesis: `Sintesi Clinica e Obiettivi di Supervisione:\nIl percorso evidenzia una buona alleanza di lavoro. Si raccomanda di mantenere il focus sull'integrazione corporea e sui temi emergenti evidenziati nella mappa semantica.`
        };
    };

    const [reportData, setReportData] = useState(getInitialReportData());

    const handleContractChange = (newContractId) => {
        setSelectedContractId(newContractId);
        setReportData(getInitialReportData(newContractId));
    };

    const handleReset = () => {
        if (window.confirm("Ripristinare tutti i testi ai valori generati automaticamente per il contratto selezionato?")) {
            setReportData(getInitialReportData(selectedContractId));
        }
    };

    const handleFieldChange = (field, value) => {
        setReportData(prev => ({ ...prev, [field]: value }));
    };

    // Download PDF with custom edits
    const handleDownloadPDF = async () => {
        if (!reportRef.current || downloading) return;
        setDownloading(true);
        try {
            const element = reportRef.current;
            const filename = `Supervisione_${reportData.clientName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
            const opt = {
                margin: [10, 10, 10, 10],
                filename: filename,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true, letterRendering: true },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
                pagebreak: { mode: ['css', 'legacy'] }
            };
            await html2pdf().set(opt).from(element).save();
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Errore durante la creazione del PDF. Puoi comunque usare la stampa del browser.');
        } finally {
            setDownloading(false);
        }
    };

    const handleCopyText = async () => {
        const text = `
=====================================================
${reportData.title} - ${reportData.subtitle}
Cliente: ${reportData.clientName}
Data: ${reportData.date} | Counselor: ${reportData.counselorName}
=====================================================

1. INQUADRAMENTO E CONTRATTO
${reportData.anagrafica}
${reportData.contract}

2. ANALISI STRUTTURA CARATTERIALE
${reportData.bioenergetics}

3. STRUMENTI DI COUNSELING
${reportData.toolsSummary}

4. MAPPA SEMANTICA & TEMI RICORRENTI
${reportData.semanticMapText}

5. UTILIZZO APP
${reportData.appUsage}

6. SINTESI CLINICA & SUPERVISIONE
${reportData.clinicalSynthesis}
=====================================================
`.trim();

        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch (err) {
            console.error('Copy error:', err);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-2 sm:p-4 backdrop-blur-sm animate-fade-in print:p-0 print:bg-white print:fixed">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[94vh] flex flex-col overflow-hidden border border-gray-100 print:max-h-none print:shadow-none print:border-none print:rounded-none">
                
                {/* Header Actions */}
                <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white p-4 sm:p-5 flex flex-wrap items-center justify-between gap-3 border-b border-indigo-900/50 print:hidden">
                    <div>
                        <h2 className="text-lg sm:text-xl font-black flex items-center gap-2">
                            <span>📄</span>
                            <span>Editor Report di Supervisione Modificabile</span>
                        </h2>
                        <p className="text-indigo-200 text-xs mt-0.5">
                            Modifica qualsiasi testo o sezione direttamente nei campi sottostanti prima di esportare.
                        </p>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        {clientContracts.length > 0 && (
                            <div className="flex items-center gap-1.5 bg-indigo-900/60 px-2.5 py-1.5 rounded-xl border border-indigo-700/50">
                                <span className="text-[10px] font-bold uppercase text-indigo-300">Contratto:</span>
                                <select
                                    value={selectedContractId}
                                    onChange={(e) => handleContractChange(e.target.value)}
                                    className="bg-slate-900 text-white text-xs font-bold rounded-lg px-2 py-1 border border-indigo-600 focus:outline-none"
                                >
                                    <option value="all">Tutti / Globale</option>
                                    {clientContracts.map(c => (
                                        <option key={c.id} value={c.id}>
                                            {c.status === 'attivo' ? '🟢' : '⚪'} {c.title}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={handleReset}
                            className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-300 transition-colors border border-slate-700"
                            title="Ripristina valori originali"
                        >
                            ↺ Reset
                        </button>
                        <button
                            type="button"
                            onClick={handleCopyText}
                            className="px-3 py-1.5 rounded-xl bg-indigo-900/80 hover:bg-indigo-800 text-xs font-semibold text-indigo-100 transition-colors border border-indigo-700 flex items-center gap-1"
                        >
                            <span>{copied ? '✓' : '📋'}</span>
                            <span>{copied ? 'Copiato!' : 'Copia Testo'}</span>
                        </button>
                        <button
                            type="button"
                            onClick={handlePrint}
                            className="px-3 py-1.5 rounded-xl bg-indigo-800/80 hover:bg-indigo-700 text-xs font-semibold text-white transition-colors border border-indigo-600 flex items-center gap-1"
                        >
                            <span>🖨️</span>
                            <span>Stampa</span>
                        </button>
                        <button
                            type="button"
                            onClick={handleDownloadPDF}
                            disabled={downloading}
                            className="px-4 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-xs font-bold text-white transition-colors shadow-md flex items-center gap-1.5 disabled:opacity-50"
                        >
                            <span>📥</span>
                            <span>{downloading ? 'Generazione PDF...' : 'Scarica PDF'}</span>
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="w-8 h-8 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-300 hover:text-white transition-colors ml-1"
                        >
                            ✕
                        </button>
                    </div>
                </div>

                {/* Printable Document Container */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-8 bg-slate-100 print:bg-white print:p-0">
                    <div
                        ref={reportRef}
                        className="max-w-[800px] mx-auto bg-white p-8 sm:p-12 rounded-2xl shadow-sm border border-gray-200 text-slate-800 space-y-6 font-sans print:p-0 print:border-none print:shadow-none print:max-w-none"
                    >
                        {/* Report Header */}
                        <div className="border-b-2 border-indigo-700 pb-4 flex justify-between items-start">
                            <div>
                                <input
                                    type="text"
                                    value={reportData.title}
                                    onChange={e => handleFieldChange('title', e.target.value)}
                                    className="text-2xl font-black uppercase text-indigo-950 tracking-tight w-full bg-transparent hover:bg-indigo-50/50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400 rounded px-1 -ml-1"
                                />
                                <input
                                    type="text"
                                    value={reportData.subtitle}
                                    onChange={e => handleFieldChange('subtitle', e.target.value)}
                                    className="text-xs font-black text-indigo-600 tracking-widest uppercase w-full bg-transparent hover:bg-indigo-50/50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400 rounded px-1 -ml-1 mt-0.5"
                                />
                            </div>
                            <div className="text-right">
                                <input
                                    type="text"
                                    value={reportData.clientName}
                                    onChange={e => handleFieldChange('clientName', e.target.value)}
                                    className="text-lg font-bold text-slate-900 text-right bg-transparent hover:bg-indigo-50/50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400 rounded px-1"
                                />
                                <div className="text-xs text-slate-400 mt-1 flex items-center justify-end gap-1">
                                    <span>Data:</span>
                                    <input
                                        type="text"
                                        value={reportData.date}
                                        onChange={e => handleFieldChange('date', e.target.value)}
                                        className="text-xs text-slate-500 font-medium text-right w-24 bg-transparent hover:bg-indigo-50/50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400 rounded px-1"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Section 1: Inquadramento e Contratto */}
                        <div className="space-y-2">
                            <h3 className="text-xs font-black uppercase tracking-wider text-indigo-800 border-b border-slate-200 pb-1">
                                1. Inquadramento e Contratto
                            </h3>
                            <input
                                type="text"
                                value={reportData.anagrafica}
                                onChange={e => handleFieldChange('anagrafica', e.target.value)}
                                className="text-xs font-semibold text-slate-700 w-full p-2 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                            <textarea
                                value={reportData.contract}
                                onChange={e => handleFieldChange('contract', e.target.value)}
                                rows="2"
                                className="text-xs text-slate-700 w-full p-2 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>

                        {/* Section 2: Struttura Caratteriale */}
                        <div className="space-y-2">
                            <h3 className="text-xs font-black uppercase tracking-wider text-indigo-800 border-b border-slate-200 pb-1">
                                2. Analisi Struttura Caratteriale
                            </h3>
                            <textarea
                                value={reportData.bioenergetics}
                                onChange={e => handleFieldChange('bioenergetics', e.target.value)}
                                rows="4"
                                className="text-xs text-slate-700 w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 leading-relaxed font-sans"
                            />
                        </div>

                        {/* Section 3: Strumenti di Counseling */}
                        <div className="space-y-2">
                            <h3 className="text-xs font-black uppercase tracking-wider text-indigo-800 border-b border-slate-200 pb-1">
                                3. Utilizzo Strumenti di Counseling
                            </h3>
                            <input
                                type="text"
                                value={reportData.toolsSummary}
                                onChange={e => handleFieldChange('toolsSummary', e.target.value)}
                                className="text-xs font-semibold text-slate-700 w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>

                        {/* Section 4: Mappa Semantica */}
                        <div className="space-y-2">
                            <h3 className="text-xs font-black uppercase tracking-wider text-indigo-800 border-b border-slate-200 pb-1">
                                4. Mappa Semantica & Temi Ricorrenti
                            </h3>
                            <textarea
                                value={reportData.semanticMapText}
                                onChange={e => handleFieldChange('semanticMapText', e.target.value)}
                                rows="2"
                                className="text-xs font-bold text-indigo-900 w-full p-3 bg-indigo-50/60 border border-indigo-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 leading-relaxed"
                            />
                        </div>

                        {/* Section 5: Utilizzo App */}
                        <div className="space-y-2">
                            <h3 className="text-xs font-black uppercase tracking-wider text-indigo-800 border-b border-slate-200 pb-1">
                                5. Utilizzo App da Parte del Cliente
                            </h3>
                            <textarea
                                value={reportData.appUsage}
                                onChange={e => handleFieldChange('appUsage', e.target.value)}
                                rows="2"
                                className="text-xs text-slate-700 w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>

                        {/* Section 6: Sintesi e Conclusioni Counselor */}
                        <div className="space-y-2">
                            <h3 className="text-xs font-black uppercase tracking-wider text-emerald-800 border-b border-slate-200 pb-1">
                                6. Sintesi Clinica & Note per la Supervisione
                            </h3>
                            <textarea
                                value={reportData.clinicalSynthesis}
                                onChange={e => handleFieldChange('clinicalSynthesis', e.target.value)}
                                rows="4"
                                className="text-xs text-emerald-950 w-full p-3 bg-emerald-50/60 border border-emerald-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400 leading-relaxed font-sans"
                            />
                        </div>

                        {/* Document Footer */}
                        <div className="pt-6 border-t border-slate-200 flex justify-between items-center text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                            <span>CONFIDENZIALE - USO PROFESSIONALE</span>
                            <span>{reportData.counselorName} • AwarenessTool Framework</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditableReportModal;
